/* ============================================
 * ALTO Header — JavaScript
 *
 * Custom elements:
 *   <alto-sticky-header>  — Sticky header behavior
 *   <alto-mega-menu>      — Desktop mega menu (slides from left)
 *   <alto-mobile-menu>    — Mobile full-screen menu (slides from right)
 * ============================================ */

/* ------------------------------------------
 * Sticky Header
 * Mirrors Dawn's StickyHeader but scoped to ALTO
 * ------------------------------------------ */
class AltoStickyHeader extends HTMLElement {
  connectedCallback() {
    this.header = document.querySelector('.section-alto-header');
    this.stickyType = this.getAttribute('data-sticky-type');
    this.headerIsAlwaysSticky = this.stickyType === 'always' || this.stickyType === 'reduce-logo-size';
    this.headerBounds = {};
    this.currentScrollTop = 0;
    this.preventReveal = false;

    this.setHeaderHeight();
    window.matchMedia('(max-width: 990px)').addEventListener('change', this.setHeaderHeight.bind(this));

    if (this.headerIsAlwaysSticky) {
      this.header.classList.add('section-alto-header-sticky');
    }

    this.onScrollHandler = this.onScroll.bind(this);
    this.hideHeaderOnScrollUp = () => (this.preventReveal = true);
    this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.addEventListener('scroll', this.onScrollHandler, false);

    // Open menu locks the page scroll (body.alto-menu-open). On iOS, unlocking
    // jumps window.scrollY, which onScroll mistakes for a scroll-down and hides
    // the header. So we pause the header while a menu is open and fix it on close.
    this.menuIsOpen = document.body.classList.contains('alto-menu-open');
    this.bodyClassObserver = new MutationObserver(this.onBodyClassChange.bind(this));
    this.bodyClassObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    this.createObserver();
  }

  onBodyClassChange() {
    const menuIsOpen = document.body.classList.contains('alto-menu-open');
    const menuJustClosed = this.menuIsOpen && !menuIsOpen;
    this.menuIsOpen = menuIsOpen;

    // We only need to step in once the menu has closed.
    if (!menuJustClosed) return;

    // Re-sync the scroll reference so the next real scroll is measured from the
    // true position, and restore header visibility.
    this.currentScrollTop = window.scrollY;
    if (this.headerIsAlwaysSticky) return;
    if (window.scrollY <= this.headerBounds.top) {
      this.reset();
    } else {
      this.reveal();
    }
  }

  setHeaderHeight() {
    document.documentElement.style.setProperty('--alto-header-height', `${this.header.offsetHeight}px`);
  }

  disconnectedCallback() {
    this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.removeEventListener('scroll', this.onScrollHandler);
    this.bodyClassObserver?.disconnect();
  }

  createObserver() {
    const observer = new IntersectionObserver((entries, obs) => {
      this.headerBounds = entries[0].intersectionRect;
      obs.disconnect();
    });
    observer.observe(this.header);
  }

  onScroll() {
    if (this.menuIsOpen) return;

    const scrollTop = window.scrollY;

    if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      this.header.classList.add('scrolled-past-header');
      if (this.preventHide) return;
      requestAnimationFrame(this.hide.bind(this));
    } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      this.header.classList.add('scrolled-past-header');
      if (!this.preventReveal) {
        requestAnimationFrame(this.reveal.bind(this));
      } else {
        window.clearTimeout(this.isScrolling);
        this.isScrolling = setTimeout(() => { this.preventReveal = false; }, 66);
        requestAnimationFrame(this.hide.bind(this));
      }
    } else if (scrollTop <= this.headerBounds.top) {
      this.header.classList.remove('scrolled-past-header');
      requestAnimationFrame(this.reset.bind(this));
    }

    this.currentScrollTop = scrollTop;
  }

  hide() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.add('section-alto-header-hidden', 'section-alto-header-sticky');
    this.closeMegaMenu();
    updateHeaderBottom();
  }

  reveal() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.add('section-alto-header-sticky', 'animate');
    this.header.classList.remove('section-alto-header-hidden');
    updateHeaderBottom();
  }

  reset() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.remove('section-alto-header-hidden', 'section-alto-header-sticky', 'animate');
    updateHeaderBottom();
  }

  closeMegaMenu() {
    const megaMenu = document.querySelector('alto-mega-menu');
    if (megaMenu) megaMenu.close();
  }
}

customElements.define('alto-sticky-header', AltoStickyHeader);

/* ------------------------------------------
 * Focus trap helper
 * Cycles Tab/Shift+Tab within a dialog panel.
 * skipMain: exclude .alto-mobile-menu__main (when sub-panel is open)
 * ------------------------------------------ */
function trapFocusKeydown(e, panel, skipMain) {
  if (e.key !== 'Tab') return;

  const focusable = Array.from(
    panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')
  ).filter((el) => {
    if (el.closest('[hidden]')) return false;
    if (skipMain && el.closest('.alto-mobile-menu__main')) return false;
    const sub = el.closest('.alto-mobile-menu__sub');
    if (sub && !sub.hasAttribute('data-active')) return false;
    return true;
  });

  if (focusable.length < 2) return;

  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
}

/* ------------------------------------------
 * Desktop Mega Menu
 * Slides from the left, 50vw, full height
 * ------------------------------------------ */
class AltoMegaMenu extends HTMLElement {
  connectedCallback() {
    this.overlay = this.querySelector('.alto-mega-menu__overlay');
    this.panel = this.querySelector('.alto-mega-menu__panel');
    this.leftPanel = this.querySelector('.alto-mega-menu__left');
    this.rightPanel = this.querySelector('.alto-mega-menu__right');
    this.activeMenuIndex = null;
    this.activeCategoryIndex = null;

    // Click outside panel closes — use document-level listener (Dawn pattern)
    this.handleDocumentClick = (e) => {
      if (!this.hasAttribute('open')) return;
      // Close if click is on overlay, or anywhere outside panel AND outside header nav buttons
      const clickedInsidePanel = this.panel && this.panel.contains(e.target);
      const clickedHeaderNav = e.target.closest('.alto-header__menu-item');
      if (!clickedInsidePanel && !clickedHeaderNav) {
        this.close();
      }
    };

    // Escape closes; Tab cycles focus within the dialog panel
    this.handleKeydown = (e) => {
      if (e.key === 'Escape') { this.close(); return; }
      trapFocusKeydown(e, this.panel, false);
    };

    // Category buttons
    this.querySelectorAll('.alto-mega-menu__category').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const menuIndex = parseInt(btn.closest('[data-menu-content]').dataset.menuContent);
        const catIndex = parseInt(btn.dataset.categoryIndex);
        this.selectCategory(menuIndex, catIndex, btn);
      });
    });
  }

  open(menuIndex) {
    // If same menu is already open, close it (toggle)
    if (this.hasAttribute('open') && this.activeMenuIndex === menuIndex) {
      this.close();
      return;
    }

    this.activeMenuIndex = menuIndex;
    this.activeCategoryIndex = null;

    // Show correct menu content
    this.querySelectorAll('[data-menu-content]').forEach((el) => {
      el.hidden = parseInt(el.dataset.menuContent) !== menuIndex;
    });

    // Reset right panel to banners
    this.resetRightPanel();

    // Reset category states
    this.querySelectorAll('.alto-mega-menu__category').forEach((btn) => {
      btn.setAttribute('aria-expanded', 'false');
    });

    // Update trigger buttons
    document.querySelectorAll('.alto-header__menu-item').forEach((btn) => {
      const idx = parseInt(btn.dataset.menuIndex);
      btn.setAttribute('aria-expanded', idx === menuIndex ? 'true' : 'false');
    });

    this.setAttribute('open', '');
    document.body.classList.add('alto-menu-open', 'alto-mega-menu-open');

    // Trap focus
    requestAnimationFrame(() => {
      const firstFocusable = this.panel.querySelector('a, button');
      if (firstFocusable) firstFocusable.focus();
    });

    document.addEventListener('keydown', this.handleKeydown);
    // Delay adding click listener so the opening click doesn't immediately close
    requestAnimationFrame(() => {
      document.addEventListener('click', this.handleDocumentClick);
    });
  }

  close() {
    this.removeAttribute('open');
    this.activeMenuIndex = null;
    this.activeCategoryIndex = null;
    document.body.classList.remove('alto-menu-open', 'alto-mega-menu-open');

    // Reset trigger buttons
    document.querySelectorAll('.alto-header__menu-item').forEach((btn) => {
      btn.setAttribute('aria-expanded', 'false');
    });

    document.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('click', this.handleDocumentClick);

    // Return focus to the trigger that opened the menu
    const activeTrigger = document.querySelector('.alto-header__menu-item[data-last-trigger]');
    if (activeTrigger) {
      activeTrigger.focus();
      activeTrigger.removeAttribute('data-last-trigger');
    }
  }

  selectCategory(menuIndex, categoryIndex, triggerBtn) {
    // Toggle off if same category clicked again
    if (this.activeCategoryIndex === categoryIndex) {
      this.activeCategoryIndex = null;
      this.resetRightPanel();
      this.querySelectorAll(`[data-menu-content="${menuIndex}"] .alto-mega-menu__category`).forEach((btn) => {
        btn.setAttribute('aria-expanded', 'false');
      });
      return;
    }

    this.activeCategoryIndex = categoryIndex;

    // Update category aria states
    this.querySelectorAll(`[data-menu-content="${menuIndex}"] .alto-mega-menu__category`).forEach((btn) => {
      btn.setAttribute('aria-expanded', parseInt(btn.dataset.categoryIndex) === categoryIndex ? 'true' : 'false');
    });

    // Show subcategory panel, hide banners
    this.rightPanel.querySelectorAll('[data-menu-banners]').forEach((el) => {
      el.hidden = true;
    });

    this.rightPanel.querySelectorAll('.alto-mega-menu__right-subcategory').forEach((el) => {
      const isMatch = parseInt(el.dataset.menuContent) === menuIndex && parseInt(el.dataset.categoryContent) === categoryIndex;
      if (isMatch) {
        el.setAttribute('data-active', '');
      } else {
        el.removeAttribute('data-active');
      }
    });
  }

  resetRightPanel() {
    // Show banners for active menu item, hide others
    this.rightPanel.querySelectorAll('[data-menu-banners]').forEach((el) => {
      el.hidden = parseInt(el.dataset.menuBanners) !== this.activeMenuIndex;
    });

    this.rightPanel.querySelectorAll('.alto-mega-menu__right-subcategory').forEach((el) => {
      el.removeAttribute('data-active');
    });
  }
}

customElements.define('alto-mega-menu', AltoMegaMenu);

/* ------------------------------------------
 * Mobile Menu
 * Slides from the right, full-screen
 * ------------------------------------------ */
class AltoMobileMenu extends HTMLElement {
  connectedCallback() {
    this.overlay = this.querySelector('.alto-mobile-menu__overlay');
    this.panel = this.querySelector('.alto-mobile-menu__panel');
    this.activeTabIndex = 0;
    this.activeSubCategory = null;

    // Close button
    this.querySelector('.alto-mobile-menu__close')?.addEventListener('click', () => this.close());

    // Overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Escape closes; Tab cycles focus — skip main view when a sub-panel is open
    this.handleKeydown = (e) => {
      if (e.key === 'Escape') { this.close(); return; }
      trapFocusKeydown(e, this.panel, this.hasAttribute('data-sub-open'));
    };

    // Tab buttons (level 1 items)
    this.querySelectorAll('.alto-mobile-menu__tab').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.switchTab(parseInt(btn.dataset.tabIndex));
      });
    });

    // Category buttons (level 2 items with children)
    this.querySelectorAll('.alto-mobile-menu__category-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const tabIndex = parseInt(btn.dataset.tabIndex);
        const catIndex = parseInt(btn.dataset.categoryIndex);
        this.showSubcategory(tabIndex, catIndex);
      });
    });

    // Back buttons
    this.querySelectorAll('.alto-mobile-menu__back').forEach((btn) => {
      btn.addEventListener('click', () => this.hideSubcategory());
    });
  }

  open() {
    this.setAttribute('open', '');
    document.body.classList.add('alto-menu-open', 'alto-mobile-menu-open');
    document.addEventListener('keydown', this.handleKeydown);

    // Reset to first tab, no subcategory.
    // TEMP: open the first *real* tab, not index 0 — when the first top-level item is a
    // [link] (direct link, no tab button/content), index 0 matches nothing and the menu
    // renders blank. The first .alto-mobile-menu__tab is the first non-[link] item (e.g. "Men").
    const firstTab = this.querySelector('.alto-mobile-menu__tab');
    this.switchTab(firstTab ? parseInt(firstTab.dataset.tabIndex) : 0);
    this.hideSubcategory();

    requestAnimationFrame(() => {
      const closeBtn = this.querySelector('.alto-mobile-menu__close');
      if (closeBtn) closeBtn.focus();
    });
  }

  close() {
    this.removeAttribute('open');
    this.hideSubcategory();
    document.body.classList.remove('alto-menu-open', 'alto-mobile-menu-open');
    document.removeEventListener('keydown', this.handleKeydown);

    // Return focus to the MENU trigger
    const trigger = document.querySelector('.alto-header__mobile-menu-trigger');
    if (trigger) trigger.focus();
  }

  switchTab(tabIndex) {
    this.activeTabIndex = tabIndex;
    this.hideSubcategory();

    // Update tab active states — toggle opacity classes directly
    this.querySelectorAll('.alto-mobile-menu__tab').forEach((btn) => {
      const isActive = parseInt(btn.dataset.tabIndex) === tabIndex;
      btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
      btn.style.opacity = isActive ? '1' : '0.5';
    });

    // Show correct tab content
    this.querySelectorAll('[data-tab-content]').forEach((el) => {
      el.hidden = parseInt(el.dataset.tabContent) !== tabIndex;
    });
  }

  showSubcategory(tabIndex, categoryIndex) {
    this.activeSubCategory = { tabIndex, categoryIndex };
    this.setAttribute('data-sub-open', '');

    // Activate the matching sub panel
    this.querySelectorAll('.alto-mobile-menu__sub').forEach((el) => {
      const isMatch = parseInt(el.dataset.tabIndex) === tabIndex && parseInt(el.dataset.categoryIndex) === categoryIndex;
      if (isMatch) {
        el.setAttribute('data-active', '');
      } else {
        el.removeAttribute('data-active');
      }
    });
  }

  hideSubcategory() {
    this.activeSubCategory = null;
    this.removeAttribute('data-sub-open');
    this.querySelectorAll('.alto-mobile-menu__sub').forEach((el) => {
      el.removeAttribute('data-active');
    });
  }
}

customElements.define('alto-mobile-menu', AltoMobileMenu);

/* ------------------------------------------
 * Init — Wire up header triggers
 * (defer guarantees DOM is parsed before this runs)
 * ------------------------------------------ */
function updateHeaderBottom() {
  const header = document.querySelector('.section-alto-header');
  if (!header) return;
  const rect = header.getBoundingClientRect();
  document.documentElement.style.setProperty('--alto-header-top', `${rect.top}px`);
  document.documentElement.style.setProperty('--alto-header-bottom', `${rect.bottom}px`);
  document.documentElement.style.setProperty('--alto-header-height', `${header.offsetHeight}px`);
}

const megaMenu = document.querySelector('alto-mega-menu');
const mobileMenu = document.querySelector('alto-mobile-menu');

// Desktop nav item clicks → open mega menu
document.querySelectorAll('.alto-header__menu-item').forEach((btn) => {
  btn.addEventListener('click', () => {
    const menuIndex = parseInt(btn.dataset.menuIndex);
    document.querySelectorAll('.alto-header__menu-item').forEach((b) => b.removeAttribute('data-last-trigger'));
    btn.setAttribute('data-last-trigger', '');
    if (megaMenu) megaMenu.open(menuIndex);
  });
});

// Region modal trigger inside the mega menu → close the mega menu so only the modal shows
if (megaMenu) {
  const regionOpener = megaMenu.querySelector('modal-opener button');
  if (regionOpener) {
    regionOpener.addEventListener('click', () => {
      // Clear the last trigger first so close() doesn't pull focus back from the modal
      document.querySelectorAll('.alto-header__menu-item').forEach((b) => b.removeAttribute('data-last-trigger'));
      megaMenu.close();
    });
  }
}

// Mobile MENU button → open mobile menu
const mobileMenuTrigger = document.querySelector('.alto-header__mobile-menu-trigger');
if (mobileMenuTrigger && mobileMenu) {
  mobileMenuTrigger.addEventListener('click', () => mobileMenu.open());
}

updateHeaderBottom();
window.addEventListener('resize', updateHeaderBottom);
